import { PERSONA_IA_OPERACIONAL } from '@modules/aawz-intelligence-v2/constants/Personas'
import { ID_SEM_MODELO } from '@modules/aawz-intelligence-v2/utils/modelos-suportados'
import { criarStateHubEAgente } from './hub-e-agente'

export default {
  conversations: [],
  conversationsLoading: false,
  conversationsLastUpdate: null,
  // Terceiro estado da lateral: distingue "não carregou" de "não existe".
  conversationsError: false,

  folders: [],
  foldersLoading: false,
  foldersLastUpdate: null,

  messages: [],
  messagesLoading: false,
  messagesLastUpdate: null,
  selectedConversation: null,

  chatLoading: false,
  responseTokens: [],
  thinkingSteps: [],
  toolCalls: [],
  // Preview de write-tool aguardando confirmacao do usuario (US-8).
  // Shape: { resumo, entidade, operacao, tool, resolved }. null quando nao ha pendencia.
  pendingConfirmation: null,

  promptSuggestions: [],
  promptSuggestionsLoading: false,
  promptSuggestionsLastUpdate: null,

  personaMode: 'profissional',

  // Persona (agente) corrente da superfície. É o valor gravado na coluna `context`.
  persona: PERSONA_IA_OPERACIONAL,
  // Agente escolhido — e não a persona dele.
  //
  // ★ Os dois existem porque `persona` é LOSSY: Painel e Consultor apontam para
  // `CONSULTOR_ESTRATEGICO`, e `agenteIdPorPersona` sempre resolve para o
  // Consultor. Quem precisa marcar o agente em uso de FORA da árvore do chat (o
  // palette da IA) erraria em silêncio num dos quatro lendo só a persona.
  //
  // ⚠️ Escrito SÓ pela superfície de navegação (`sidebarComoNavegacao`), mesmo
  // gate do bus da busca global: popup e painel do Hub dividem este namespace e
  // recebem agente por prop — deixá-los escrever aqui faria uma instância que o
  // usuário nem vê mudar o que o palette marca.
  agenteAtivoId: '',
  // Cliente que dá ESCOPO à conversa. `null` = conversa geral (do escritório).
  // Mora no store, e não na view, porque o composer (que escolhe) e o cartão de
  // contexto (que mostra) são componentes irmãos, em alturas diferentes da
  // árvore — passar por props atravessaria o motor do chat inteiro.
  clienteEscopo: null,
  // Cliente que a IA resolveu DENTRO da conversa via `abrir_cliente` (spec-016
  // T035, QA it.3) — distinto de `clienteEscopo` (esse é ESCOLHIDO pelo
  // usuário no seletor). No `/ia` imersivo, `abrir_cliente` sempre abre em
  // nova aba e a aba de ORIGEM nunca ficava sabendo qual cliente a IA tinha
  // resolvido; este estado fecha o buraco. Shape:
  // `{id_cliente_app, codigo, nome, conversation_id}` | null. Por conversa —
  // `view-chat.vue` recarrega/limpa ao trocar de conversa (ver
  // `restaurarEstadoDaConversa`).
  clienteDaConversa: null,
  // spec-009 [T006] — cliente FIXADO MANUALMENTE pelo consultor NESTA
  // conversa (seletor de escopo, ou `fixarClienteDaConversa` quando o
  // seletor destrava numa conversa sem vínculo). Sustenta o GUARD do
  // AC-2.3: enquanto ele apontar para outro cliente NESTA conversa, um
  // evento `cliente-identificado` de cliente diferente não pode sobrescrever
  // o chip — é o que impede reabrir o G-1 ("escritório inteiro não é
  // sobrescrito pela navegação", `aawz-intelligence-v2/CLAUDE.md:493`).
  // Shape: `{conversation_id, id_cliente_app}` | null. Limpo junto com
  // `clienteDaConversa` em `restaurarEstadoDaConversa`/`novaConversa`.
  clienteFixadoManualmente: null,
  // Itens do `tool_result` de `citar_servicos` do turno EM ANDAMENTO. Transiente por
  // construção — a verdade persistida mora em `tool_calls` da mensagem.
  servicosCitadosStreaming: [],
  // ★ Resolvido no merge com o `desenvolvimento` (#1961): os dois lados criaram
  // `painelStreaming` com o MESMO nome e shapes diferentes — o nosso guardava o
  // estado ja reduzido, o dele guarda a transcricao crua e reduz no getter
  // `painelParcialStreaming`. Como o painel que ficou foi o dele, vale a forma
  // dele: `utils/painel-eventos.js` e `extrair-painel.js` (nossos) sairam.
  // spec-008: perguntas ao gestor do turno em andamento (viram formulário).
  perguntasAoGestorStreaming: [],
  // Transcrição do painel deliberativo do turno EM ANDAMENTO. Mesma natureza
  // transiente: a verdade persistida mora em `tool_calls` da mensagem, e
  // `extrairPainel` a lê de lá quando a conversa é reaberta.
  painelStreaming: null,
  // Eventos `painel_evento` do turno EM ANDAMENTO, na ordem de chegada — a sala
  // do comitê montando ao vivo. O getter `painelParcialStreaming` os reduz ao
  // shape da transcrição; quando o evento `painel` final chega, a casca passa a
  // preferi-lo ao parcial (`messages.vue`), e esta lista é esvaziada no `end` do
  // turno — junto com `painelStreaming`, pela mesma razão: daí em diante a sala
  // vive na mensagem, em `tool_calls`.
  painelEventosStreaming: [],
  // Fase do turno em streaming (spec-007 R1): null | { phase, label } — a
  // pílula do header. Derivada client-side dos eventos SSE (utils/fase-do-turno).
  faseDoTurno: null,
  // ★ [review #2364, item D] O último `node-status` do turno em streaming:
  // null | { no, estado } (`constants/NosDoSupervisor.js`). Alimentado por
  // `apiChat._onNodeStatus`, zerado com a fase (`_resetarFase`) — é o degrau
  // da linha de raciocínio entre a tool em voo e a fase
  // (`utils/rotulo-do-turno.js`).
  noDoTurno: null,
  // Fechamento contábil do turno (spec-001 `plataforma-por-chat`, evento SSE
  // `finish`): null | { stop_reason, usage, custo_brl }. Quem FECHA o turno
  // continua sendo o `end` — este campo só guarda o que o `end` não traz
  // (consumo e custo), e é sobrescrito a cada turno novo.
  finishDoTurno: null,

  // O Progress da preparação de reunião e demais atalhos que publicam passos
  // de negócio (spec-001 `preparacao-reuniao`, D8.2, evento SSE `plano`,
  // `core/plano_vivo.py`): null | { versao, itens: [{id, texto, estado}] }.
  // `versao` só CRESCE — o dedupe mora em `apiChat.js::_onPlano` (descarta
  // evento com `versao` menor ou igual à corrente, senão um evento atrasado
  // faria o Progress voltar no tempo). Zerado ao fim do turno, junto com
  // `_painel`/`_painelSeqVisto` — o Progress é só do turno AO VIVO.
  planoDoTurno: null,

  // D6 (spec-010 `supervisor-e-agentes-de-dominio`, [T028]) + RF-G2 — hub do
  // turno, agente por mensagem e a lista AO VIVO do fan-out. Substituem o par
  // `persona`/`agenteAtivoId` abaixo, que o D6 aposenta: o usuário não escolhe
  // mais agente, o Supervisor roteia por turno.
  //
  // As três chaves saem do slice COMPARTILHADO `hub-e-agente.js`, e de uma
  // FUNÇÃO: `agentePorMensagem` é um mapa, e um mapa compartilhado entre os
  // três namespaces do chat faria o badge de uma mensagem do CRM aparecer sobre
  // a resposta do drawer do Fee Fixo. A documentação de cada campo mora lá.
  ...criarStateHubEAgente(),

  // ★ SEM literal de id de modelo (Q7-A08, 03/09/2026). O padrão vem de
  // `GET /supported-models` e é aplicado no `SET_SUPPORTED_MODELS`, que também
  // DESCARTA um id salvo que saiu da lista — foi assim que `claude-sonnet-4-6`
  // sobreviveu no `localStorage` de quem já usava o chat e passou a mandar todo
  // turno com um modelo que o backend recusa (400). Ver
  // `utils/modelos-suportados.js`.
  //
  // Vazio até a lista chegar: quem envia o turno faz `selectedModel || null`, e
  // sem id o backend aplica o padrão DELE — que é o desfecho certo.
  selectedModel: (function() {
    try {
      return localStorage.getItem('iaCrmSelectedModel') || ID_SEM_MODELO
    } catch (e) {
      console.warn('[ia-crm] localStorage read iaCrmSelectedModel falhou — sem modelo até a lista chegar', e)
      return ID_SEM_MODELO
    }
  })(),
  supportedModels: [],
  supportedModelsLoaded: false,

  thinkingEnabled: (function() {
    try {
      const stored = localStorage.getItem('iaCrmThinkingEnabled')
      return stored === null ? true : stored !== 'false'
    } catch (e) {
      console.warn('[ia-crm] localStorage read iaCrmThinkingEnabled falhou — usando default', e)
      return true
    }
  })(),

  // Voz da IA no Modo de Voz (spec-016 T004, ADR-016-02). Mesma receita de
  // `selectedModel`/`thinkingEnabled`: preferência do consultor, persistida em
  // `localStorage` — a última escolhida vira o default da PRÓXIMA conversa
  // (US-1 AC3). String vazia = "nenhuma escolhida" (US-1 AC2): o consumidor
  // faz `vozSelecionada || null` antes de mandar no pedido de síntese, e sem
  // a chave o backend sintetiza na voz padrão — byte a byte o de hoje.
  //
  // ⚠️ `localStorage` pode lançar (modo anônimo, dados bloqueados) — o
  // `try/catch` garante que a tela renderiza igual (voz padrão), nunca quebra.
  vozSelecionada: (function() {
    try {
      return localStorage.getItem('iaCrmVozSelecionada') || ''
    } catch (e) {
      console.warn('[ia-crm] localStorage read iaCrmVozSelecionada falhou — sem voz escolhida', e)
      return ''
    }
  })(),

  // Curadoria de sugestões de IA (spec-003) — fila mutável, sem cache.
  curadoriaGroups: [],
  curadoriaSummary: null,
  curadoriaLoading: false,
  // spec-006: a tela de triagem tem abas (A triar / Aceitas / Descartadas) e uma
  // barra de progresso — precisa dos três recortes ao mesmo tempo, não só da fila.
  curadoriaPorStatus: { aguardando: [], aceita: [], declinada: [] },

  // ---- Modo Agente (spec-002-chat-agentico-estilo-claude-code, plan.md D13) --
  // Run acompanhado pela superfície principal (US-13): `{id_run, seq, estado,
  // motivo}`. `null` enquanto nenhum run foi aceito nesta conversa/sessão —
  // é o que faz `run-status-chip.vue` sumir (`view-chat.vue::mostrarChipRun`).
  runAtual: null,
  // Mapa por `id_proposta` — N cards, N decisões independentes ([adv C-25]).
  // Shape de cada entrada: { id_proposta, hash_payload, expira_em, estado:
  // 'aberta'|'decidida'|'invalidada', resultado, recibo, preview,
  // substituidaPor }. NUNCA um singleton — `pendingConfirmation` continua
  // sendo isso, para o fluxo legado do Modo Chat.
  propostasAbertas: {},
  // W3 — banner discreto durante a reconexão por cursor após EOF sem
  // terminal ([adv C-30]). `view-chat.vue::reconectando`.
  reconectando: false,
  // TodoWrite/TodoListMiddleware do turno em andamento (US-10) — substituído
  // pela versão de `versao` maior (nunca regride; o mesmo turno pode repetir
  // o evento `plano`). A versão persistida mora em `message.plano`.
  plano: null,
  // Canais transientes do Modo Agente que só REGISTRAM — a renderização é de
  // outra task (001/003). Arrays na ordem de chegada, esvaziados a cada turno.
  buscaStreaming: [],
  codigoStreaming: [],
  arquivoStreaming: [],

  // Insights da lateral imersiva. Chaves FLAT como todo o resto deste state —
  // o `{ data, loading, lastUpdate }` aninhado do gerador quebraria a simetria
  // com as outras 25 chaves. `insightsError` existe porque sem o terceiro
  // estado "não carregou" fica indistinguível de "não existe", e uma coluna
  // vazia lê como escritório sem dado.
  insights: [],
  insightsLoading: false,
  insightsError: false,
  insightsLastUpdate: null,

  // Faixa no topo do composer: sugestões da IA aguardando decisão. `null` =
  // NÃO apurado, diferente de `0` (apurado e fila vazia) — ver
  // `utils/sugestoes-a-triar.js`.
  sugestoesATriar: null,
  sugestoesATriarLoading: false,

  // Vínculo WhatsApp da IA do profissional logado (`GET /whatsapp/vinculo`),
  // carregado UMA vez por sessão por `apiWhatsappVinculo.garantir` e mantido em dia
  // pelo modal Conectar WhatsApp. `status`: null (não apurado) | 'nenhum' |
  // 'pendente' | 'ativo'. Quem precisa saber "tem WhatsApp conectado?" (botão
  // de enviar PDF, por exemplo) lê `whatsappConectado`, nunca chama a API.
  whatsappVinculo: { status: null, carregado: false },
}
